import React, { useEffect, useMemo, useState } from 'react';
import {
  subscribeDocumentPresence,
  isFreshDocumentCursor,
  type DocumentCursorPresence,
} from '../../utils/documentPresenceService';
import './DocumentPresenceLayer.css';

interface DocumentPresenceLayerProps {
  documentId: string;
  pageNumber: number;
  selfUid?: string;
}

/**
 * Renders peer cursors over a PDF page. Positions are stored in normalized
 * page coordinates (0–100) and map directly to percentage left/top.
 */
const DocumentPresenceLayer: React.FC<DocumentPresenceLayerProps> = ({
  documentId,
  pageNumber,
  selfUid = '',
}) => {
  const [cursors, setCursors] = useState<DocumentCursorPresence[]>([]);
  const [, setTick] = useState(0);

  useEffect(
    () => subscribeDocumentPresence(documentId, selfUid, setCursors),
    [documentId, selfUid],
  );

  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 5000);
    return () => clearInterval(t);
  }, []);

  const fresh = useMemo(() => {
    const now = Date.now();
    return cursors.filter(
      c => c.pageNumber === pageNumber && isFreshDocumentCursor(c, now),
    );
  }, [cursors, pageNumber]);

  if (fresh.length === 0) return null;

  return (
    <div className="document-presence-layer" aria-hidden>
      {fresh.map(c => (
        <div
          key={c.uid}
          className="document-presence-cursor"
          style={{ left: `${c.x}%`, top: `${c.y}%` }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" className="document-presence-arrow">
            <path
              d="M5.5 3.2L19 11.4l-6.3 1.5-3.2 5.8z"
              fill={c.color}
              stroke="#fff"
              strokeWidth="1.4"
            />
          </svg>
          <span className="document-presence-name" style={{ background: c.color }}>
            {c.name}
          </span>
        </div>
      ))}
    </div>
  );
};

export default React.memo(DocumentPresenceLayer);
